import type { ReactElement, ReactNode } from 'react';

export type LegalArticle = {
  id: string;
  title: string;
  body: ReactNode;
};

/**
 * 약관 문서 공용 레이아웃.
 *
 * 약관은 스캔이 아니라 "찾아 읽는" 문서다. 조문 번호를 고정하고 목차에서
 * 바로 뛸 수 있게 한다 — 분쟁이 생기면 조문 단위로 인용된다.
 */
export function LegalDoc({
  title,
  effectiveDate,
  notice,
  articles,
}: {
  title: string;
  effectiveDate: string;
  notice?: ReactNode;
  articles: LegalArticle[];
}): ReactElement {
  return (
    <div className="u-container grid gap-10 py-12 lg:grid-cols-[16rem_minmax(0,1fr)] lg:items-start">
      <nav aria-label="목차" className="lg:sticky lg:top-24">
        <p className="mb-3 font-mono text-[10px] text-muted-foreground uppercase tracking-[0.12em]">
          목차
        </p>
        <ul className="flex flex-col gap-1.5">
          {articles.map((article, index) => (
            <li key={article.id}>
              <a
                href={`#${article.id}`}
                className="block text-[13px] text-muted-foreground hover:text-foreground"
              >
                제{index + 1}조 {article.title}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <article className="flex max-w-[46rem] flex-col gap-8">
        <header className="flex flex-col gap-2">
          <h1 className="text-[clamp(1.5rem,1.2rem+1vw,2rem)] font-bold tracking-[-0.04em]">
            {title}
          </h1>
          <p className="text-[13px] text-muted-foreground">시행일 {effectiveDate}</p>
        </header>

        {notice ? (
          <div className="rounded-lg border border-caution border-l-[3px] p-4 text-[13px] leading-[1.8]">
            {notice}
          </div>
        ) : null}

        {articles.map((article, index) => (
          <section key={article.id} id={article.id} className="flex scroll-mt-24 flex-col gap-3">
            <h2 className="text-[1.0625rem] font-semibold tracking-[-0.028em]">
              제{index + 1}조 ({article.title})
            </h2>
            <div className="flex flex-col gap-2 text-[14px] leading-[1.9] text-muted-foreground">
              {article.body}
            </div>
          </section>
        ))}
      </article>
    </div>
  );
}
